import React, { useEffect, useState } from 'react';
import { useOwned } from './useCrm';
import { auth } from '../../firebaseConfig';
import { watchPath } from '../../services/crmService';
import { ui, MUTED, DANGER } from './crmUi';
import { canMessageLead } from '../../shared/leadPermissions';

// Who an email blast would reach. Archived contacts get nothing; no email, opted out, or consent
// turned off are skipped. The server checks all of this again at send time.
export const blastSkipReason = (c) => (c.archived ? 'archived' : !String(c.email || '').trim() ? 'no email' : c.email_opt_out ? 'opted out of email' : c.consentEmail === false ? 'no email consent' : null);

const post = async (body) => {
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch('/api/send-blast', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

// Eastern Time date + time -> ISO (handles EDT/EST).
const etToIso = (date, time) => {
  const guess = new Date(`${date}T${time}:00Z`);
  const etParts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(guess);
  const g = Object.fromEntries(etParts.map((p) => [p.type, p.value]));
  const asEt = Date.UTC(+g.year, +g.month - 1, +g.day, +g.hour % 24, +g.minute);
  return new Date(guess.getTime() + (guess.getTime() - asEt)).toISOString();
};
const fmtEt = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET';

export default function ListEmailBlast({ list, members, uid, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [step, setStep] = useState('compose'); // compose | preview
  const [when, setWhen] = useState('now');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [blasts, setBlasts] = useState([]);
  const own_Blasts = useOwned('crm/emailBlasts');
  useEffect(() => setBlasts(own_Blasts), [own_Blasts]);

  // Only leads this agent is assigned to or entered themselves (admins: everyone).
  const go = members.filter((c) => !blastSkipReason(c) && canMessageLead(c, uid, isAdmin));
  const skipped = members.length - go.length;
  const scheduled = blasts.filter((b) => b.listId === list.id && ['scheduled', 'awaiting_approval'].includes(b.status) && (isAdmin || b.createdBy === uid));

  const confirmSend = async () => {
    const sendAt = when === 'later' ? etToIso(date, time) : null;
    const what = when === 'later' ? `schedule this email to ${go.length} contact(s) for ${fmtEt(sendAt)}` : `send this email now to ${go.length} contact(s)`;
    if (!window.confirm(`Are you sure you want to ${what}?\n\nIf test mode is on, every copy goes only to the test inbox.`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await post({ action: 'create', listId: list.id, listName: list.name, contactIds: go.map((c) => c.id), subject, body, ...(sendAt ? { sendAt } : {}) });
      setMsg(r.queued ? `Ready for approval: open "Approve email" in Today's Tasks to preview and send it${r.sendAt ? ` (scheduled ${fmtEt(r.sendAt)})` : ''}. Nothing goes out until you approve. Unapproved emails expire after 3 days.` : r.scheduled ? `Scheduled for ${fmtEt(r.sendAt)}. You can cancel it below until then.` : `Done: ${r.sentCount || 0} sent${r.testMode ? ' (test mode: all went to the test inbox)' : ''}, ${Object.values(r.skipped || {}).reduce((a, b) => a + b, 0)} skipped.`);
      setStep('compose'); setOpen(false); setSubject(''); setBody('');
    } catch (e) { setMsg(`Not sent: ${e.message}`); } finally { setBusy(false); }
  };
  const cancel = async (b) => {
    if (!window.confirm(`Cancel the scheduled email "${b.subject}" (${fmtEt(b.sendAt)})?`)) return;
    try { await post({ action: 'cancel', id: b.id }); setMsg('Cancelled. Nothing was sent.'); } catch (e) { setMsg(`Could not cancel: ${e.message}`); }
  };

  return (
    <div>
      <button style={ui.btn} onClick={() => { setOpen(!open); setStep('compose'); setMsg(''); }}>Send Email Blast</button>
      {msg && <p style={{ fontSize: 13, margin: '6px 0' }}>{msg}</p>}
      {scheduled.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <strong style={{ fontSize: 13 }}>Scheduled for this list</strong>
          {scheduled.map((b) => (
            <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '4px 0' }}>
              <span>{fmtEt(b.sendAt)}: "{b.subject}" ({(b.contactIds || []).length} contacts)</span>
              <button style={ui.ghost} onClick={() => cancel(b)}>Cancel</button>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div style={{ ...ui.card, marginTop: 10 }}>
          {step === 'compose' ? (<>
            <label style={ui.label}>Subject</label>
            <input style={ui.input} value={subject} onChange={(e) => setSubject(e.target.value)} />
            <label style={ui.label}>Message (use {'{first name}'} for each person's first name)</label>
            <textarea style={{ ...ui.input, minHeight: 160 }} value={body} onChange={(e) => setBody(e.target.value)} />
            <button style={{ ...ui.btn, marginTop: 8 }} disabled={!subject.trim() || !body.trim()} onClick={() => setStep('preview')}>Preview</button>
          </>) : (<>
            <p style={{ margin: '0 0 6px' }}><strong>Will go to {go.length} contact(s).</strong> {skipped > 0 && <span style={{ color: MUTED }}>{skipped} skipped (archived, no email, opted out, or no email consent).</span>}</p>
            <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 10, background: '#fff' }}>
              <div style={{ fontWeight: 700 }}>{subject}</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginTop: 6 }}>{body.replace(/\{\{?\s*first ?name\s*\}?\}/gi, (go[0]?.firstName || 'there'))}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
              <label style={{ fontSize: 14 }}><input type="radio" checked={when === 'now'} onChange={() => setWhen('now')} /> Send now</label>
              <label style={{ fontSize: 14 }}><input type="radio" checked={when === 'later'} onChange={() => setWhen('later')} /> Schedule (Eastern Time)</label>
              {when === 'later' && <>
                <input type="date" style={{ ...ui.input, width: 'auto' }} value={date} onChange={(e) => setDate(e.target.value)} />
                <input type="time" style={{ ...ui.input, width: 'auto' }} value={time} onChange={(e) => setTime(e.target.value)} />
              </>}
            </div>
            {when === 'later' && <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>Scheduled emails go out within 15 minutes of the time you pick. Anyone archived, opted out, or without an email by then is skipped.</p>}
            {go.length === 0 && <p style={{ color: DANGER }}>No one on this list can be emailed.</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={ui.ghost} onClick={() => setStep('compose')}>Back</button>
              <button style={ui.btn} disabled={busy || !go.length || (when === 'later' && !date)} onClick={confirmSend}>{busy ? 'Working...' : when === 'later' ? 'Schedule' : 'Send now'}</button>
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}
